<template>
  <div class="flex flex-col w-screen h-screen overflow-hidden bg-gray-50">
    <BookUserBar v-if="customer" class="flex-none h-10 bg-accent" />

    <!-- Loading -->
    <div v-if="loading" class="p-8">
      <Loading />
    </div>

    <div v-else class="relative flex flex-col flex-grow w-full h-full mx-auto overflow-y-auto">
      <div class="flex flex-col h-full lg:grid lg:grid-cols-5">
        <div class="lg:col-span-3">
          <!-- Steps -->
          <div class="flex justify-around text-sm font-medium text-center text-gray-700 bg-gray-100" v-if="steps">
            <div 
              v-for="(step, index) in steps" :key="step.name" 
              class="relative flex justify-center flex-grow h-12">
              <div 
                class="flex flex-col items-center justify-center flex-grow pb-1 border-t-2 sm:flex-row" 
                :class="{ 'text-accent border-accent bg-white': currentStep ? step.route === currentStep.route : false }">
                <fa 
                  :icon="['far', 'check']" 
                  fixed-width
                  class="transition duration-200 delay-200 transform sm:-translate-x-5 text-md" 
                  :class="currentIndex > index ? 'scale-100' : 'scale-0'" />
                <div class="sm:-ml-4 md:block">
                  {{ step.name }}
                </div>
              </div>
            </div>
          </div>
          
          <!-- Title -->
          <div class="p-4 font-medium text-center bg-white shadow-sm" v-if="currentStep">
            {{ currentStep.title }}
          </div>
          
          <!-- Forms -->
          <div class="pb-20 lg:py-6">
            <transition :name="transitionName">
              <router-view :key="$route.name" />
            </transition>
          </div>
        </div>
        <!-- Summary -->
        <div class="fixed top-0 z-10 w-screen h-screen overflow-y-auto transition-transform duration-500 ease-in-out transform bg-gray-100 border-l lg:overflow-visible lg:pt-0 lg:h-full lg:sticky lg:top-0 lg:transform-none lg:w-auto lg:block lg:col-span-2" :class="showSummary ? 'translate-y-0' : 'translate-y-full'">
          <button class="absolute top-0 right-0 z-30 block w-16 h-16 ml-auto text-xl font-bold text-gray-800 lg:hidden" @click="showSummary = false">
            &#10005;
          </button>
          <div class="relative z-20 h-full bg-gray-100">
            <BookSummary />
          </div>
        </div>
        <!-- Backdrop -->
        <transition
          enter-active-class="transition duration-500"
          enter-to-class="opacity-100"
          enter-class="opacity-0"
          leave-active-class="transition duration-500"
          leave-to-class="opacity-0"
          leave-class="opacity-100">
          <div class="fixed inset-0 bg-black bg-opacity-75 lg:hidden" v-if="showSummary"></div>
        </transition>
        <!-- Show Summary (Mobile) -->
        <div class="fixed z-20 w-full transform -translate-y-12 lg:hidden top-full">
          <button class="flex items-center justify-center w-full h-12 p-4 font-medium border-t bg-gradient-to-b from-white to-gray-100" @click="showSummary = !showSummary">
            {{ showSummary ? 'Hide' : 'Show' }} Summary <div class="ml-2 transition duration-500 ease-out transform" :class="showSummary ? 'rotate-180': 'rotate-0'">&uarr;</div>
          </button>
        </div>
      </div>
    </div>
    
  </div>
</template>

<script>
  import { mapActions, mapState, mapGetters, mapMutations } from 'vuex'
  import BookSummary from '@/views/book/BookSummary'
  import BookUserBar from '@/views/book/BookUserBar'

  export default {
    name: 'Booking',

    metaInfo () {
      const accountName = this.portal.name
      return {
        title: 'Booking',
        titleTemplate: `%s | ${accountName}`
      }
    },

    components: {
      BookSummary,
      BookUserBar
    },

    async created () {
      await this.getFormOptions()
      
      if (this.$cookies.get('customer_token')) {
        await this.syncCustomer()
      }
    },

    async mounted () {
      // Populate test data
      if (this.$route.query.test && process.env.NODE_ENV === 'development') {
        this.loadTestData()
      }

      this.migrate()
    },

    computed: {
      ...mapState(['colors', 'portal']),
      ...mapState('booking', ['form', 'options']),
      ...mapState('customer', ['customer']),
      ...mapGetters('booking', ['steps', 'currentStep', 'currentIndex'])
    },

    data () {
      return {
        loading: true,
        submitting: false,
        transitionName: 'slide-right',
        showSummary: false
      }
    },

    methods: {
      ...mapActions('customer', ['syncCustomer']),
      ...mapMutations('booking', ['updateOptions', 'loadTestData', 'resetForm', 'migrate']),

      async getFormOptions () {
        try {
          const { data } = await this.$api.get(`book/${this.$route.params.id}`)
          this.updateOptions(data.options)
          document.documentElement.style.setProperty('--accent-color', this.colors[data.options.color])
          if (this.steps && this.$route.name !== this.steps[0].route) {
            this.$router.replace({ name: this.steps[0].route })
          }
          this.loading = false
        } catch (error) {
          console.error(error)
        }
      },
      
      next () {
        if (this.currentIndex === this.steps.length) { return false }
        let nextStep = this.steps[this.currentIndex + 1]
        this.$router.push({ name: nextStep.route })
      },

      back () {
        if (this.currentIndex < 1) { return false }
        let previousStep = this.steps[this.currentIndex - 1]
        this.$router.push({ name: previousStep.route })
      },

      async submit () {
        let post = {
          ...this.form, 
          type: this.options.type 
        }

        for (let origin of post.origins) {
          if (!origin.formatted_address) {
            origin.formatted_adress = `${origin.address_1}, ${origin.city}, ${origin.country}, ${origin.zip}`
          }
        }

        for (let destination of post.destinations) {
          if (!destination.formatted_address) {
            destination.formatted_adress = `${destination.address_1}, ${destination.city}, ${destination.country}, ${destination.zip}`
          }
        }

        /**
         * Log to logger for debugging
         */
        this.$api.post(`logger`, {
          title: 'New Booking Submission',
          body: post
        })
        
        try {
          this.submitting = true
          const { data } = await this.$api.post(`book/${this.$route.params.id}`, post)
          this.resetForm()
          this.$router.push({ name: 'BookSuccess', query: { booking: data.booking.uuid } })
        } catch (error) {
          console.error(error)
          this.$toasted.error(error.response.data.message)
        } finally {
          this.submitting = false
        }
      }
    },

    watch: {
      '$route' (to, from) {
        if (to.name !== from.name) {
          const toStep = this.steps.findIndex(step => step.route === to.name)
          const fromStep = this.steps.findIndex(step => step.route === from.name)
          this.transitionName = toStep < fromStep ? 'slide-right' : 'slide-left'
          this.showSummary = false
        }
      }
    }
  }
</script>

<style lang="postcss">
/* Animations */
.slide-right-enter-active,
.slide-left-enter-active {
  @apply transform transition duration-200 ease-in-out delay-100;
}

.slide-right-leave-active,
.slide-left-leave-active {
  @apply transform transition duration-100 ease-in-out;
}

.slide-right-enter {
  @apply opacity-0 -translate-x-10;
}

.slide-right-leave-to {
  @apply opacity-0 translate-x-10;
}

.slide-left-enter {
  @apply opacity-0 translate-x-10;
}

.slide-left-leave-to {
  @apply opacity-0 -translate-x-10;
}

.slide-right-enter-to,
.slide-left-enter-to  {
  @apply opacity-100 translate-x-0;
}

.slide-right-leave,
.slide-left-leave {
  @apply opacity-100 translate-x-0;
}
</style>